import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, Badge, Button, Input, Select } from '../components/ui';
import { api, ApiError } from '../lib/api';
import type { ApiResponse } from '../lib/types';

interface DestSet {
  id: string;
  name: string;
  type: string;
  client_name?: string;
  status?: string;
  tracking_code?: string;
}

const PAGE_SIZES = [25, 50, 100];

export default function Destinations() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<DestSet[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // withCounts=false: esta lista não mostra items_count, então não paga
  // a agregação no OpenSearch.
  const load = useCallback(() => {
    setLoading(true);
    api
      .get<ApiResponse<DestSet[]> & { total?: number }>(
        `/destinations/list?withCounts=false&limit=${pageSize}&offset=${page * pageSize}`
      )
      .then((r) => {
        setRows(r.data || []);
        setTotal(Number(r.total) || 0);
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Failed to load destinations'))
      .finally(() => setLoading(false));
  }, [page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const visible = q
    ? rows.filter((d) => `${d.name} ${d.client_name || ''} ${d.type} ${d.tracking_code || ''}`.toLowerCase().includes(q))
    : rows;

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, page * pageSize + rows.length);

  return (
    <Layout title="Destinations" actions={<Button onClick={() => navigate('/destinations/new')}>+ Add Set</Button>}>
      {error && <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div className="md:col-span-3">
          <Input placeholder="Filter this page by name, client, type…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={String(pageSize)} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}>
          {PAGE_SIZES.map((s) => <option key={s} value={s}>{s} per page</option>)}
        </Select>
      </div>

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="text-sm text-slate-500">
            {loading ? 'Loading…' : `${from}–${to} of ${total}`}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={loading || page === 0}>‹ Previous</Button>
            <span className="text-xs text-slate-500 tabular-nums">Page {page + 1} / {pages}</span>
            <Button variant="outline" onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={loading || page + 1 >= pages}>Next ›</Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-surface-muted">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Client</th>
                <th className="px-4 py-3 font-medium">Tracking Code</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && <tr><td colSpan={6} className="px-4 py-6 text-slate-400">Loading…</td></tr>}
              {!loading && visible.map((d) => (
                <tr key={d.id} className="hover:bg-surface-muted/60">
                  <td className="px-4 py-3 font-medium text-ink-900">{d.name}</td>
                  <td className="px-4 py-3"><Badge tone={d.type === 'feed' ? 'blue' : 'neutral'}>{d.type}</Badge></td>
                  <td className="px-4 py-3 text-slate-500">{d.client_name || '—'}</td>
                  <td className="px-4 py-3 text-slate-400 font-mono text-xs max-w-xs truncate">{d.tracking_code || '—'}</td>
                  <td className="px-4 py-3"><Badge tone={d.status === 'active' ? 'green' : 'neutral'}>{d.status || 'active'}</Badge></td>
                  <td className="px-4 py-3 text-right"><div className="flex justify-end gap-1.5"><Button variant="outline" onClick={() => navigate(`/destinations/edit/${d.id}`)}>Edit</Button><Button variant="ghost" onClick={() => navigate(`/destinations/view/${d.id}`)}>View</Button></div></td>
                </tr>
              ))}
              {!loading && visible.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No destination sets found.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </Layout>
  );
}
